/**
 * Pure helper that classifies a `PinnedObjectRef` against the TanStack Query
 * cache so the explorer tree can render the three pin states:
 *
 *  - `unresolved` — the parent listing isn't in cache (disconnected, or the
 *                   folder/db hasn't been expanded yet). We genuinely don't
 *                   know whether the pinned target exists.
 *  - `resolved`   — the parent listing IS cached, AND the pinned target was
 *                   found inside it.
 *  - `stale`      — the parent listing IS cached, but the pinned target is
 *                   NOT present. The underlying object is gone.
 *
 * Synchronous, side-effect-free: only reads `queryClient.getQueryData(...)`,
 * never triggers a fetch. Call sites are responsible for ensuring the parent
 * queries have been issued (the live tree normally does).
 *
 * Query-key shapes mirror `src/features/database-objects/hooks/useDatabaseQueries.ts`:
 *   - databases: `['database-objects', 'databases', connectionId]`
 *   - objects:   `['database-objects', objectType, connectionId, database]`
 * where `objectType` is the same `DatabaseObjectType` literal used by the live
 * tree (e.g. `'table'`, `'view'`, `'stored-procedure'`, `'function'`).
 */

import type { QueryClient } from '@tanstack/react-query';
import type { PinnedObjectRef } from '../../../../types/electron';
import type { PinResolution } from '../../../explorer/models/nodeTypes';
import { isWorkspacePinRef } from './pinRef';
import { resolveStoragePin } from '../azureStorageListing';
import { databaseObjectsQueryKey } from '../databaseObjectsQueryKeys';

/**
 * The `ObjectKind` literal used inside `PinnedObjectRef` (the durable on-disk
 * shape) differs from the `DatabaseObjectType` literal used as the second
 * segment of the TanStack Query key. Map them here so the cache lookup
 * matches what `useDatabaseQueries.fetchObjects` writes.
 */
type ObjectKindFromRef = Extract<PinnedObjectRef, { kind: 'object' }>['objectType'];

const OBJECT_QUERY_KEY: Record<ObjectKindFromRef, string> = {
  table: 'table',
  view: 'view',
  storedProcedure: 'stored-procedure',
  function: 'function',
};

/**
 * Extract the database-name list from whatever shape the databases query has
 * cached. The real `fetchDatabases` returns a `GetDatabasesResult` envelope
 * whose `.databases` is `string[]`. Tests sometimes seed bare `{name}[]` or
 * bare `string[]` to drive the resolver directly.
 *
 * Returns `null` when the cache is empty (caller treats as `unresolved`).
 */
function readDatabaseNames(raw: unknown): string[] | null {
  if (raw == null) return null;
  // Envelope: GetDatabasesResult { databases: string[] }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const inner = (raw as { databases?: unknown }).databases;
    if (Array.isArray(inner)) {
      // Review finding #14 — treat an empty listing as "we don't know yet"
      // (`unresolved`), not "no such database" (`stale`). An in-flight refresh
      // or permission-blip can momentarily resolve with `{databases: []}`;
      // collapsing every pin to `stale` for that frame produces a visible
      // all-pins flash-to-greyed on cold connect.
      if (inner.length === 0) return null;
      return inner.map((d) => (typeof d === 'string' ? d : (d as { name?: string })?.name ?? ''));
    }
    return null;
  }
  // Bare array of strings or { name } objects.
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null;
    return raw.map((d) => (typeof d === 'string' ? d : (d as { name?: string })?.name ?? ''));
  }
  return null;
}

export function resolvePin(
  ref: PinnedObjectRef,
  queryClient: QueryClient,
  connectionId: string,
): PinResolution {
  // F7 — workspace pins (tableView/query/notebook) are self-contained: they
  // carry their own payload and reference no live schema object, so there is
  // no parent listing to look up in the query cache. They are always
  // 'resolved'. `buildPinnedChildren` never routes a workspace ref here (it
  // returns the node directly), but keep this early return as defense so a
  // future caller can't reach the object-assuming branch below (which reads
  // `ref.objectType`, undefined for a workspace ref).
  if (isWorkspacePinRef(ref)) return 'resolved';

  // Azure Storage pins resolve from the shared listing cache (spec §5.1 keys).
  if (ref.kind === 'storage') return resolveStoragePin(ref.resource, queryClient, connectionId);

  const dbNames = readDatabaseNames(
    queryClient.getQueryData(databaseObjectsQueryKey.databases(connectionId)),
  );

  if (ref.kind === 'database') {
    if (!dbNames) return 'unresolved';
    return dbNames.includes(ref.database) ? 'resolved' : 'stale';
  }

  if (ref.kind === 'folder') {
    if (!dbNames) return 'unresolved';
    return dbNames.includes(ref.database) ? 'resolved' : 'stale';
  }

  if (ref.kind === 'schema') {
    // A schema is "resolved" if its parent database exists. A stronger
    // check (the schema actually appears in the database's object set)
    // requires the preloaded-objects cache; the database-exists signal is
    // the same gate the folder branch uses, and matches the practical
    // user contract: pin stays alive as long as the database does.
    if (!dbNames) return 'unresolved';
    return dbNames.includes(ref.database) ? 'resolved' : 'stale';
  }

  // ref.kind === 'object'
  const parentList = queryClient.getQueryData<{ schema: string; name: string }[]>(
    // Cast: the shared key fn returns the same shape the resolver needs.
    // The DatabaseObjectType literal that the producer uses matches the
    // string we map to via `OBJECT_QUERY_KEY` (table / view / stored-procedure
    // / function).
    databaseObjectsQueryKey.objects(
      OBJECT_QUERY_KEY[ref.objectType] as Parameters<typeof databaseObjectsQueryKey.objects>[0],
      connectionId,
      ref.database,
    ) as unknown as readonly unknown[],
  );
  if (!parentList) return 'unresolved';
  return parentList.some((o) => o.schema === ref.schema && o.name === ref.name)
    ? 'resolved'
    : 'stale';
}
